import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BankInfo } from '../../../types/verification';
import { AlertCircle, CheckCircle, Building2, CreditCard, Building, Globe, MapPin } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';

type Props = {
  bankInfo: BankInfo;
  setBankInfo: (info: BankInfo) => void;
  onSubmit: (contractUrl?: string) => void;
  onBack: () => void;
  contractUrl?: string; // Add contractUrl prop
};

type ValidationErrors = {
  [K in keyof BankInfo]?: string;
};

export const BankStep = ({ 
  bankInfo, 
  setBankInfo, 
  onSubmit, 
  onBack,
  contractUrl // Add contractUrl to props
}: Props) => {
  const [touched, setTouched] = useState<{ [K in keyof BankInfo]?: boolean }>({});
  const [errors, setErrors] = useState<ValidationErrors>({});
  const { translations } = useLanguage();

  const validateField = (field: keyof BankInfo, value: string): string => {
    switch (field) {
      case 'bankName':
        return !value.trim() ? translations?.bankStep?.errors?.bankNameRequired || 'Bank name is required' : '';
      case 'accountNumber':
        // Allow both IBAN format and regular account numbers
        const ibanRegex = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;
        const accountRegex = /^\d{8}$|^\d{9}$|^\d{10,20}$/;
        return (!ibanRegex.test(value) && !accountRegex.test(value)) 
          ? translations?.bankStep?.errors?.accountNumberInvalid || 'Invalid account number format (IBAN or 8-9 digits or 10-20 digits)' 
          : '';
      case 'swiftCode':
        return !/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(value) ? translations?.bankStep?.errors?.swiftCodeInvalid || 'Invalid SWIFT/BIC code format' : '';
      case 'bankBranch':
        return !value.trim() ? translations?.bankStep?.errors?.bankBranchRequired || 'Bank branch is required' : '';
      case 'ifscCode':
        return value.trim() ? (/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value) ? '' : translations?.bankStep?.errors?.ifscCodeInvalid || 'Invalid IFSC code format') : '';
      default:
        return '';
    }
  };

  const handleChange = (field: keyof BankInfo, value: string) => {
    const newBankInfo = { ...bankInfo, [field]: value };
    setBankInfo(newBankInfo);
    
    const error = validateField(field, value);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleBlur = (field: keyof BankInfo) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, bankInfo[field]);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const isFormValid = () => {
    const requiredFields: (keyof BankInfo)[] = ['bankName', 'accountNumber', 'swiftCode', 'bankBranch'];
    const newErrors: ValidationErrors = {};
    let isValid = true;

    requiredFields.forEach(field => {
      const error = validateField(field, bankInfo[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    // IFSC is optional but validate if provided
    if (bankInfo.ifscCode) {
      const ifscError = validateField('ifscCode', bankInfo.ifscCode);
      if (ifscError) {
        newErrors.ifscCode = ifscError;
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = () => {
    const allTouched = Object.keys(bankInfo).reduce(
      (acc, field) => ({ ...acc, [field]: true }),
      {}
    );
    setTouched(allTouched);

    if (isFormValid()) {
      onSubmit(contractUrl); // Pass the contractUrl to onSubmit
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <h2 className="text-2xl font-bold">
        {translations?.bankStep?.headers?.bankInformation || 'Bank Information'}
      </h2>

      <div className="space-y-6 max-w-md">
        {renderInput(
          'bankName',
          translations?.bankStep?.labels?.bankName || 'Bank Name',
          <Building2 size={20} />,
          translations?.bankStep?.placeholders?.bankName || 'Enter your bank name'
        )}
        {renderInput(
          'accountNumber',
          translations?.bankStep?.labels?.accountNumber || 'Account Number',
          <CreditCard size={20} />,
          translations?.bankStep?.placeholders?.accountNumber || 'Enter IBAN or account number',
          true
        )}
        {renderInput(
          'swiftCode',
          translations?.bankStep?.labels?.swiftCode || 'SWIFT/BIC Code',
          <Globe size={20} />,
          translations?.bankStep?.placeholders?.swiftCode || 'e.g., BOFAUS3N'
        )}
        {renderInput(
          'bankBranch',
          translations?.bankStep?.labels?.bankBranch || 'Bank Branch',
          <Building size={20} />,
          translations?.bankStep?.placeholders?.bankBranch || 'Enter bank branch name'
        )}
        {renderInput(
          'ifscCode',
          translations?.bankStep?.labels?.ifscCode || 'RTGS/NEFT/IFSC Code',
          <MapPin size={20} />,
          translations?.bankStep?.placeholders?.ifscCode || 'e.g., ABCD0123456',
          false
        )}
      </div>

      <div className="flex justify-between pt-6">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onBack}
          className="px-6 py-2 text-primary-500 border border-primary-500 rounded-lg hover:bg-primary-50 transition-colors"
        >
          {translations?.bankStep?.buttons?.back || 'Back'}
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleSubmit}
          className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
        >
          {translations?.bankStep?.buttons?.submit || 'Submit'}
        </motion.button>
      </div>
    </motion.div>
  );

  function renderInput(
    field: keyof BankInfo,
    label: string,
    icon: React.ReactNode,
    placeholder: string = '',
    required: boolean = true,
    pattern?: string
  ) {
    const hasError = touched[field] && errors[field];
    const isValid = touched[field] && !errors[field];

    return (
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
          {!required && <span className="text-gray-400 text-xs ml-2">(Optional)</span>}
        </label>
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            {icon}
          </div>
          <input
            type="text"
            value={bankInfo[field]}
            onChange={(e) => handleChange(field, e.target.value)}
            onBlur={() => handleBlur(field)}
            placeholder={placeholder}
            pattern={pattern}
            className={`
              w-full pl-10 pr-10 py-2 rounded-lg border transition-colors
              ${hasError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
              ${isValid ? 'border-green-500 focus:border-green-500 focus:ring-green-500' : ''}
              ${!touched[field] ? 'border-gray-300 focus:border-primary-500 focus:ring-primary-500' : ''}
            `}
          />
          {hasError && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <AlertCircle className="text-red-500" size={20} />
            </div>
          )}
          {isValid && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <CheckCircle className="text-green-500" size={20} />
            </div>
          )}
        </div>
        {hasError && (
          <p className="mt-1 text-sm text-red-500">{errors[field]}</p>
        )}
      </div>
    );
  }
};
